import React, { useState, useRef, useEffect, useMemo } from 'react';
import './CustomMultiSelect.css';

export type Option = {
  id:string
  label: string;
  value: string;
};

interface CustomMultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled : boolean
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = 'Select options',
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnOutsideClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  /**
   * toggle dropdown
   * @returns 
   */
  const toggleDropdown = () => {
    if(disabled){
      return;
    }
    if(options.length > 0){
      setIsOpen(prev => !prev);
    }
  }

  /**
   * handle select
   * @param value 
   * @returns 
   */
  const handleSelect = (id: string) => {
    if(options.length == 1 && options[0].id === 'N/A'){
       return;
    }
    if (selected.includes(id)) {
      onChange(selected.filter(v => v !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  // Sorted drop down list item by label albhabetically 
  const sortedOptions = useMemo(() => {
    return options.slice().sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);

  return (
    <div className="custom-multiselect" ref={ref}>
      <div className={`custom-multiselect-toggle d-flex justify-content-between ${disabled ? 'disabled' : ''}`} onClick={toggleDropdown}>
        {selected.length
          ? sortedOptions.filter(o => selected.includes(o.id)).map(o => o.label).join(', ')
          : placeholder}
        <span className="arrow no-translate">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && !disabled && (
        <div className="custom-multiselect-menu">
          {sortedOptions.map(opt => (
            <div key={opt.value} className="dropdown-item d-flex gap-2" onClick={() => handleSelect(opt.id)}>
              {sortedOptions && sortedOptions[0].value !== 'N/A' && <input type="checkbox" checked={selected.includes(opt.id)} readOnly />}
              <span>{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomMultiSelect);
